// Data-fetching React hooks for the thin-client viewer.
//
// Each hook owns one resource: it fetches on mount, exposes
// {data, loading, error}, and re-fetches when its inputs change.
// AbortController cancels in-flight requests on unmount or
// dependency change so the user can scroll workspaces/conversations
// without races.
//
// We don't memoize across hook instances — separate <Component>
// trees that mount the same hook each pay for their own fetch.
// That's fine for the data sizes involved (workspaces list is
// tiny, conversations list small, messages paginated). A future
// optimization could hoist results to a context cache if needed.

import { useCallback, useEffect, useRef, useState } from "react";

import {
  ApiError,
  type ApiConversation,
  type ApiMessage,
  type ApiUser,
  type ThreadResponse,
  type WorkspaceSummary,
  getAttachmentBlob,
  getThread,
  listConversations,
  listMessages,
  listUsers,
  listWorkspaces,
} from "./api";

interface AsyncResult<T> {
  data: T | null;
  loading: boolean;
  error: ApiError | null;
}

interface AsyncResultWithRefresh<T> extends AsyncResult<T> {
  refresh: () => void;
}

// ---------- workspaces ------------------------------------------------------

/**
 * Fetches /workspaces. ``refresh`` re-runs the fetch (used after
 * a delete so the rail picks up the removed entry).
 *
 * Pass 3.8 cutover: the AppContext-side ``workspacesRefreshKey``
 * is gone with the IngestPanel — the hook's own ``refresh`` handle
 * is the only trigger now.
 */
export function useWorkspaces(): AsyncResultWithRefresh<WorkspaceSummary[]> {
  const [data, setData] = useState<WorkspaceSummary[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiError | null>(null);
  const [tick, setTick] = useState(0);

  const refresh = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    const ctrl = new AbortController();
    setLoading(true);
    setError(null);
    listWorkspaces(ctrl.signal)
      .then((r) => {
        if (ctrl.signal.aborted) return;
        setData(r.workspaces);
        setLoading(false);
      })
      .catch((e) => {
        if (ctrl.signal.aborted) return;
        if (e instanceof ApiError) setError(e);
        setLoading(false);
      });
    return () => ctrl.abort();
  }, [tick]);

  return { data, loading, error, refresh };
}

// ---------- conversations ---------------------------------------------------

export function useConversations(
  teamID: string | null,
): AsyncResult<ApiConversation[]> {
  const [data, setData] = useState<ApiConversation[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  useEffect(() => {
    if (!teamID) {
      setData(null);
      setLoading(false);
      setError(null);
      return;
    }
    const ctrl = new AbortController();
    setLoading(true);
    setError(null);
    listConversations(teamID, ctrl.signal)
      .then((r) => {
        if (ctrl.signal.aborted) return;
        setData(r.conversations);
        setLoading(false);
      })
      .catch((e) => {
        if (ctrl.signal.aborted) return;
        if (e instanceof ApiError) setError(e);
        setLoading(false);
      });
    return () => ctrl.abort();
  }, [teamID]);

  return { data, loading, error };
}

// ---------- users -----------------------------------------------------------

export function useUsers(
  teamID: string | null,
): AsyncResult<Record<string, ApiUser>> {
  const [data, setData] = useState<Record<string, ApiUser> | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  useEffect(() => {
    if (!teamID) {
      setData(null);
      setLoading(false);
      setError(null);
      return;
    }
    const ctrl = new AbortController();
    setLoading(true);
    setError(null);
    listUsers(teamID, ctrl.signal)
      .then((r) => {
        if (ctrl.signal.aborted) return;
        setData(r.users);
        setLoading(false);
      })
      .catch((e) => {
        if (ctrl.signal.aborted) return;
        if (e instanceof ApiError) setError(e);
        setLoading(false);
      });
    return () => ctrl.abort();
  }, [teamID]);

  return { data, loading, error };
}

// ---------- messages (paginated) -------------------------------------------

interface MessagesPageState {
  /** All messages loaded so far, ascending by ts. The hook reverses
   *  each descending-order page from the service before storing,
   *  so MessagePane can render top-down chronologically without
   *  caring about the underlying fetch direction. */
  messages: ApiMessage[];
  loading: boolean;
  loadingMore: boolean;
  error: ApiError | null;
  /** True if the service still has older messages we haven't
   *  fetched yet. Drives the "Load older messages" affordance
   *  in MessagePane. In around mode this is hasMoreBefore. */
  hasMore: boolean;
  /** Pass 2.13: only meaningful when targetTs was used to seed the
   *  hook. True if newer messages exist past the loaded window. */
  hasMoreAfter: boolean;
  /** Trigger fetching the next batch OLDER than the currently-
   *  oldest loaded message. No-op while loadingMore or when
   *  hasMore is false. Pass 2.6 inverted the direction; pre-2.6
   *  this loaded the next NEWER batch, which conflicted with the
   *  button label and the Slack-native UX of opening to the most
   *  recent state. */
  loadMore: () => void;
  /** Pass 2.13: load a batch newer than the currently-newest loaded
   *  message. Only meaningful when the hook was seeded with
   *  targetTs (the around-fetch path) — otherwise the initial page
   *  is already the newest and there's nothing newer to load. */
  loadNewer: () => void;
  /** Pass 2.13: the ts the hook is currently anchored on. Mirrors
   *  the parent's ``initialTargetTs`` prop one-for-one — Pass
   *  2.13.4 dropped the internal-state model in favour of using
   *  the prop directly so prop changes (sidebar conv switch,
   *  cross-workspace jump) flow through reliably. MessagePane's
   *  scroll-to-ts effect watches this to know when to re-scroll. */
  targetTs: string | null;
}

// Pass 2.13.2: load the whole conversation in a single round-trip.
// The service cap is now 10000 (raised from 500); even the largest
// observed conversation in the corpus is ~930 messages, so a single
// fetch returns everything and has_more is always false. The
// follow-on loadMore/loadNewer paths stay in the code as a defensive
// fallback if the cap is ever exceeded — UI never renders them at
// today's scale because has_more / has_more_after stay false.
const PAGE_SIZE = 10000;
// Around mode now also loads everything — a search jump centres on
// the target ts but the user can scroll the full conversation freely
// from there without hitting "Load newer / older" affordances.
const AROUND_PAGE_SIZE = 10000;

/**
 * Loads messages in ascending ts order, but fetches them
 * NEWEST-FIRST so the initial open lands on the most recent
 * conversation state — matching how Slack itself renders.
 *
 * Internal model:
 *   - state.messages stays sorted ascending by ts (the rendering
 *     side never has to think about direction)
 *   - the service is queried with direction='older':
 *       * initial fetch (no cursor) → newest PAGE_SIZE in
 *         descending order; we reverse and store
 *       * loadMore(cursor) → next PAGE_SIZE older than cursor in
 *         descending order; we reverse and PREPEND to messages
 *   - cursor tracked here is the boundary ts to feed to the next
 *     'older' query (i.e. the ts of the currently-oldest loaded
 *     message); the service returns it as next_cursor on each page
 *
 * Pass 2.6 (the bug this fixes): pre-2.6 the hook fetched
 * direction='newer' with no cursor — which returns the OLDEST
 * PAGE_SIZE — and "loadMore" appended the next NEWER batch. The
 * button label said "Load older messages" but the implementation
 * walked forward in time. Evidence review started in 2024 instead
 * of recent state, and pagination drove the wrong direction.
 */
export function useMessagesPage(
  teamID: string | null,
  convID: string | null,
  initialTargetTs?: string | null,
): MessagesPageState {
  const [messages, setMessages] = useState<ApiMessage[]>([]);
  // hasMore drives the "Load older" affordance. In default mode it
  // also reflects the cursor's exhaustion; in around mode it
  // mirrors has_more_before from the service.
  const [hasMore, setHasMore] = useState(false);
  const [hasMoreAfter, setHasMoreAfter] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  // Pass 2.13: track the ts boundaries directly (not the opaque
  // cursor) so loadMore/loadNewer can hand the boundary ts to the
  // service via direction=older/newer&cursor=<ts>. This was always
  // workable in the default flow too — the "cursor" the service
  // returns is just base64(ts) — and tracking the ts directly
  // means around mode gets follow-on pagination for free without
  // a second cursor format.
  const [oldestTs, setOldestTs] = useState<string | null>(null);
  const [newestTs, setNewestTs] = useState<string | null>(null);
  // Pass 2.13.4 (BUG 4 + BUG 6): the target ts is now driven by
  // the parent prop directly. Pre-fix the hook held it as internal
  // state seeded once at mount, so subsequent prop changes
  // (cross-workspace jump where activeConversation resolves a
  // render later, sidebar conv click dropping the prop back to
  // null) were silently ignored — the around-fetch fired with a
  // stale ts and 404'd in the new conversation. The reset effect
  // below now depends on ``initialTargetTs`` so prop changes flow
  // through; ``targetTs`` is exposed for MessagePane's scroll
  // effect and tracks the prop one-for-one.
  const targetTs = initialTargetTs ?? null;

  // Track the previous (teamID, convID) so the reset effect can
  // tell "the conversation changed" (clear stale messages) from
  // "only the target ts changed" (keep messages stable while the
  // around-fetch lands — Pass 2.13.4 BUG 3).
  const prevConvKeyRef = useRef<string | null>(null);

  // Reset effect — fires on conv change OR on targetTs change. The
  // initial fetch path branches on targetTs: null → default older-
  // newest fetch (Slack-native UX), non-null → around fetch.
  useEffect(() => {
    if (!teamID || !convID) {
      setMessages([]);
      setOldestTs(null);
      setNewestTs(null);
      setHasMore(false);
      setHasMoreAfter(false);
      setLoading(false);
      setError(null);
      prevConvKeyRef.current = null;
      return;
    }
    const convKey = `${teamID}::${convID}`;
    const isConvChange = prevConvKeyRef.current !== convKey;
    prevConvKeyRef.current = convKey;

    const ctrl = new AbortController();
    setLoading(true);
    setError(null);
    if (isConvChange) {
      // Different conversation — old messages are wrong for the
      // new one. Clear so the body doesn't briefly render stale
      // content from the previous conv.
      setMessages([]);
      setOldestTs(null);
      setNewestTs(null);
      setHasMore(false);
      setHasMoreAfter(false);
    }
    // Pass 2.13.4 BUG 3: when ONLY initialTargetTs changes (same
    // conv, search-jump on a message we already loaded), KEEP
    // messages + counts visible. The header would otherwise flash
    // "0 messages" between effect-start and fetch-resolve. The
    // upcoming fetch will atomically swap the data when it lands.

    if (targetTs) {
      // Around mode — service returns ascending order already; no
      // reverse needed. has_more_before/after replace has_more.
      listMessages(teamID, convID, {
        limit: AROUND_PAGE_SIZE,
        direction: "around",
        targetTs,
        signal: ctrl.signal,
      })
        .then((r) => {
          if (ctrl.signal.aborted) return;
          const msgs = r.messages;
          setMessages(msgs);
          setOldestTs(msgs.length > 0 ? msgs[0]!.ts : null);
          setNewestTs(msgs.length > 0 ? msgs[msgs.length - 1]!.ts : null);
          setHasMore(r.has_more_before === true);
          setHasMoreAfter(r.has_more_after === true);
          setLoading(false);
        })
        .catch((e) => {
          if (ctrl.signal.aborted) return;
          if (e instanceof ApiError) setError(e);
          setLoading(false);
        });
    } else {
      // Default — fetch the newest PAGE_SIZE in descending order,
      // reverse to ascending. hasMoreAfter is always false here
      // (we're already at the newest end).
      listMessages(teamID, convID, {
        limit: PAGE_SIZE,
        direction: "older",
        signal: ctrl.signal,
      })
        .then((r) => {
          if (ctrl.signal.aborted) return;
          const ascending = [...r.messages].reverse();
          setMessages(ascending);
          setOldestTs(ascending.length > 0 ? ascending[0]!.ts : null);
          setNewestTs(
            ascending.length > 0 ? ascending[ascending.length - 1]!.ts : null,
          );
          setHasMore(r.has_more);
          setHasMoreAfter(false);
          setLoading(false);
        })
        .catch((e) => {
          if (ctrl.signal.aborted) return;
          if (e instanceof ApiError) setError(e);
          setLoading(false);
        });
    }
    return () => ctrl.abort();
  }, [teamID, convID, initialTargetTs]);

  const loadMore = useCallback(() => {
    if (!teamID || !convID) return;
    if (loadingMore || !hasMore || oldestTs === null) return;
    const ctrl = new AbortController();
    setLoadingMore(true);
    // Service expects opaque cursor for older direction; the cursor
    // it returns is base64(ts), but we hold the boundary ts and
    // re-encode it. Encoding here matches the service's
    // _encode_cursor in api/conversations.py.
    const cursor = btoa(oldestTs);
    listMessages(teamID, convID, {
      limit: PAGE_SIZE,
      direction: "older",
      cursor,
      signal: ctrl.signal,
    })
      .then((r) => {
        if (ctrl.signal.aborted) return;
        const olderAscending = [...r.messages].reverse();
        setMessages((prev) => [...olderAscending, ...prev]);
        if (olderAscending.length > 0) {
          setOldestTs(olderAscending[0]!.ts);
        }
        setHasMore(r.has_more);
        setLoadingMore(false);
      })
      .catch((e) => {
        if (ctrl.signal.aborted) return;
        if (e instanceof ApiError) setError(e);
        setLoadingMore(false);
      });
  }, [teamID, convID, loadingMore, hasMore, oldestTs]);

  const loadNewer = useCallback(() => {
    if (!teamID || !convID) return;
    if (loadingMore || !hasMoreAfter || newestTs === null) return;
    const ctrl = new AbortController();
    setLoadingMore(true);
    const cursor = btoa(newestTs);
    listMessages(teamID, convID, {
      limit: PAGE_SIZE,
      direction: "newer",
      cursor,
      signal: ctrl.signal,
    })
      .then((r) => {
        if (ctrl.signal.aborted) return;
        // Service returns ascending for direction='newer'; append.
        const newerAscending = r.messages;
        setMessages((prev) => [...prev, ...newerAscending]);
        if (newerAscending.length > 0) {
          setNewestTs(newerAscending[newerAscending.length - 1]!.ts);
        }
        setHasMoreAfter(r.has_more);
        setLoadingMore(false);
      })
      .catch((e) => {
        if (ctrl.signal.aborted) return;
        if (e instanceof ApiError) setError(e);
        setLoadingMore(false);
      });
  }, [teamID, convID, loadingMore, hasMoreAfter, newestTs]);

  return {
    messages,
    loading,
    loadingMore,
    error,
    hasMore,
    hasMoreAfter,
    loadMore,
    loadNewer,
    targetTs,
  };
}

// ---------- thread ----------------------------------------------------------

export function useThreadDetail(
  teamID: string | null,
  convID: string | null,
  parentTs: string | null,
): AsyncResult<ThreadResponse> {
  const [data, setData] = useState<ThreadResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  useEffect(() => {
    if (!teamID || !convID || !parentTs) {
      setData(null);
      setLoading(false);
      setError(null);
      return;
    }
    const ctrl = new AbortController();
    setLoading(true);
    setError(null);
    getThread(teamID, convID, parentTs, ctrl.signal)
      .then((r) => {
        if (ctrl.signal.aborted) return;
        setData(r);
        setLoading(false);
      })
      .catch((e) => {
        if (ctrl.signal.aborted) return;
        if (e instanceof ApiError) setError(e);
        setLoading(false);
      });
    return () => ctrl.abort();
  }, [teamID, convID, parentTs]);

  return { data, loading, error };
}

// ---------- attachment blob URLs (authed) ----------------------------------

/**
 * Fetches an attachment binary with the bearer token attached,
 * wraps it in a URL.createObjectURL, and revokes the URL on
 * unmount. The returned URL goes straight into <img src>, <video
 * src>, etc.
 *
 * Why this hook exists: <img src="https://service/files/X"> would
 * NOT carry the Authorization header. A service worker could
 * inject it but adds operational complexity. The Object-URL
 * approach trades a bit of memory for a self-contained component.
 */
export function useAuthedBlobUrl(
  teamID: string | null,
  fileID: string | null,
): { url: string | null; loading: boolean; error: ApiError | null } {
  const [url, setUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  useEffect(() => {
    if (!teamID || !fileID) {
      setUrl(null);
      setLoading(false);
      setError(null);
      return;
    }
    const ctrl = new AbortController();
    let createdUrl: string | null = null;
    setLoading(true);
    setError(null);
    setUrl(null);
    getAttachmentBlob(teamID, fileID, ctrl.signal)
      .then((blob) => {
        if (ctrl.signal.aborted) return;
        createdUrl = URL.createObjectURL(blob);
        setUrl(createdUrl);
        setLoading(false);
      })
      .catch((e) => {
        if (ctrl.signal.aborted) return;
        if (e instanceof ApiError) setError(e);
        setLoading(false);
      });
    return () => {
      ctrl.abort();
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [teamID, fileID]);

  return { url, loading, error };
}

// Pass 3.8 cutover: ``useSnapshots``, ``useMergeLog``, and the
// ``useDriveStatus`` polling hook used to live here. They wrapped
// ZIP-pipeline endpoints (``listSnapshots``, ``getMergeLog``,
// ``getDriveStatus``) and were consumed by the now-deleted
// chain-of-custody / Drive-backup UI in WorkspaceInfoPanel.

// ---------- avatar palette --------------------------------------------------

const PALETTE_SIZE = 6;

/**
 * Build the per-workspace palette index map (user_id → 0..5) from
 * a users dict, by sorted user_id position. Mirrors the algorithm
 * Pass 1.5 used at parse time for the local archive — same input
 * produces the same color assignment, so the rendered avatars are
 * stable across viewer/service splits.
 */
export function buildAvatarPaletteByUserID(
  users: Record<string, ApiUser>,
): Record<string, number> {
  const ids = Object.keys(users).sort();
  const out: Record<string, number> = {};
  ids.forEach((id, idx) => {
    out[id] = idx % PALETTE_SIZE;
  });
  return out;
}

